import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import Dropdown from '@comp/Dropdown/Dropdown';
import { useDispatchToStore, useSelectorToStore } from '@hooks/reduxHooks';
import { fetchSeeAllTypeFeed } from '@actions/specialClasses';
import segmentStorage from '@events/segmentStorage';
import { breakpoints, Spacings } from '@styles/index';
import { seeAllTypes } from '@constants/index';
import {
  isEducatorFeed,
  isClassFeed,
  isBatchFeed,
  isTopicFeed,
  isTestSeriesFeed,
  getBlockInfoKey,
  getSeeAllFeedKey,
  isSubjectFeed
} from '@utils/feedTypeHelper';
import PopularSubjects from '@cont/FeedTypeBlock/PopularSubjects';
import { getIsGTPGoal } from '@api/selectors/constants';
import { setSelectedNav } from '@stores/sidenav';
import plusNavBarItems from '@layout/NavBar/PlusNavBar/plusNavBarItems';
import { segment } from '@actions/analytics';
import { FILTER_APPLIED, FREE_CLASSES_AND_TESTS } from '@constants/segment';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useConstants from '@api/hooks/useConstants';
import TopBlock from './TopBlock';
import EducatorFeed from './EducatorFeed';
import ClassFeedBlock from './ClassFeedBlock';
import TestSeriesFeedBlock from './TestSeriesFeedBlock';
import TopicFeedBlock from './TopicFeedBlock';

const DropdownWrapper = styled.div`
  grid-column: span 18;
  display: grid;
  grid-template-columns: repeat(18, 1fr);
  grid-column-gap: ${Spacings.SPACING_4B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-bottom: ${Spacings.SPACING_6B};
  }
`;

const filterMenuItems = (tabs) =>
  tabs.map((tab) => ({ ...tab, value: tab.label.toLowerCase() }));

const SeeAll = ({ seeAllType, useLeaderboardMetaInfo, pageType }) => {
  const router = useRouter();
  const dispatch = useDispatchToStore();
  let { topicSlugType, goalUID, type } = router.query;
  if (
    seeAllType !== seeAllTypes.GOAL &&
    seeAllType !== seeAllTypes.SPECIAL_CLASS
  )
    type = topicSlugType;

  const isGTP = useConstants(getIsGTPGoal(goalUID));
  const goalSlug = 'iit-jee';
  const goalInfo = useSelectorToStore(
    ({ topologies }) => topologies.topologyDetails[goalUID]
  );
  const classes = useSelectorToStore(
    ({ paginator }) => paginator.seeAllTypeFeed
  );
  const popularSubjects = useSelectorToStore(
    ({ courses }) => courses.allPopularSubjects
  );
  const blockInfo = useSelectorToStore(
    ({ courses }) =>
      courses.seeAllFeedEBInfo[getBlockInfoKey(seeAllType, goalUID, type)]
  );
  const blockId = blockInfo.id;
  const [selectedMenu, changeSelectedClassType] = useState({
    value: blockInfo?.tabs?.[0].label.toLowerCase(),
    ...blockInfo.tabs?.[0]
  });
  const goalSegmentData = useGoalEventProps({ goalUID });

  useEffect(() => {
    if (selectedMenu.label) segmentStorage.setItem('lpss', selectedMenu.label);
    if (isGTP) setSelectedNav(plusNavBarItems.BROWSE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMenu.label, isGTP]);

  const defaultBlockURL = blockInfo?.tabs?.[0]?.url;
  if (!goalUID || !type || !blockInfo) return null;

  const isDropdownRequired = blockInfo?.tabs?.length > 1;

  let key = getSeeAllFeedKey(
    isDropdownRequired,
    seeAllType,
    goalUID,
    type,
    selectedMenu.value
  );

  const handleButtonClick = (option) => {
    changeSelectedClassType(option);
    key = isDropdownRequired ? `${type}-${option.value}` : type;
    dispatch(
      fetchSeeAllTypeFeed(goalUID, key, option.url, blockId, seeAllType)
    );
    if (isGTP) {
      segment.track(FILTER_APPLIED, {
        ...goalSegmentData,
        last_primary_source: FREE_CLASSES_AND_TESTS,
        filter_type: 'Status',
        filter_value: option.label
      });
    }
  };

  const menuItems = filterMenuItems(blockInfo.tabs);

  const label =
    isGTP && isClassFeed(blockInfo.entity_type)
      ? 'Free Classes'
      : blockInfo.label;

  return (
    <>
      <TopBlock
        label={label}
        goal={goalInfo}
        seeAllType={seeAllType}
        isDropdown={isDropdownRequired}
        pageType={pageType}
      />
      {isDropdownRequired && (
        <DropdownWrapper>
          <Dropdown
            options={menuItems}
            onOptionClick={handleButtonClick}
            selectedOption={menuItems.find(
              (menuItem) => menuItem.value === selectedMenu.value
            )}
            gridColumn="span 5"
            menuWidth="220px"
          />
        </DropdownWrapper>
      )}
      {isEducatorFeed(blockInfo.entity_type) && (
        <EducatorFeed
          goalSlug={goalSlug}
          goalUID={goalUID}
          educatorFeedBlock={classes}
          routerQuery={router.query}
          selectedMyClassType={selectedMenu.value}
          blockId={blockId}
          defaultBlockURL={defaultBlockURL}
          seeAllType={seeAllType}
          blockType={type}
          goalkey={key}
          useLeaderboardMetaInfo={useLeaderboardMetaInfo}
        />
      )}
      {isSubjectFeed(blockInfo.entity_type) && (
        <PopularSubjects feedBlock={{ data: popularSubjects }} isFeed={false} />
      )}
      {(isClassFeed(blockInfo.entity_type) ||
        isBatchFeed(blockInfo.entity_type)) && (
        <ClassFeedBlock
          classFeedBlock={classes}
          goalSlug={goalSlug}
          goalUID={goalUID}
          goalkey={key}
          blockId={blockId}
          defaultBlockURL={defaultBlockURL}
          seeAllType={seeAllType}
          blockType={
            type !== 'my_classes' ? type : `${type}-${selectedMenu?.value}`
          }
          dropdownOption={isDropdownRequired ? selectedMenu.value : ''}
          label={selectedMenu.label}
        />
      )}
      {isTestSeriesFeed(blockInfo.entity_type) && (
        <TestSeriesFeedBlock
          testSeriesFeedBlock={classes}
          goalSlug={goalSlug}
          goalUID={goalUID}
          goalkey={key}
          blockId={blockId}
          defaultBlockURL={defaultBlockURL}
          seeAllType={seeAllType}
          blockType={type}
          label={selectedMenu.label}
        />
      )}
      {isTopicFeed(blockInfo.entity_type) && (
        <TopicFeedBlock
          testSeriesFeedBlock={classes}
          goalSlug={goalSlug}
          goalUID={goalUID}
          goalkey={key}
          blockId={blockId}
          defaultBlockURL={defaultBlockURL}
          seeAllType={seeAllType}
          blockType={type}
          onClick={() => {}}
          label={selectedMenu.label}
        />
      )}
    </>
  );
};

export default SeeAll;

SeeAll.propTypes = {
  seeAllType: PropTypes.string.isRequired,
  useLeaderboardMetaInfo: PropTypes.bool
};

SeeAll.defaultProps = {
  useLeaderboardMetaInfo: false
};
